import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, RefreshCw, Loader2, Bike, X, Navigation, Undo2, LocateFixed, AlertCircle,
  Eye, EyeOff, Pause, Play, Flag, Maximize2, Minimize2, Repeat, PackageOpen, UserPlus,
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addGoogleBaseLayer } from '../../utils/googleBaseLayer';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS, FLEET_APP_EVENTS, emitFleetAppEvent } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { FLEET_PERMISSION, can } from '../../utils/fleetPermissions';
import {
  COLOR_STORAGE_KEY, COURIER_COLORS, UNASSIGNED_COLOR, assignCourierColors, byReceiptNo, courierDisplayName,
  formatDuration, formatRouteLabel, freshness, isAtRestaurant, lastLocationText, locationRequestCoolingDown,
  nearestWithin, ordersSignature, packageSummary, packageTitle, receiptLabel, relativeTime, sanitizeCourierColors,
  shortCustomerName, waitHint, waitingAtRestaurant,
} from './liveMapRules';
import { courierIcon, dropoffIcon, restaurantIcon, routeLabelIcon, unassignedIcon } from './liveMapIcons';
import { AssignCourierModal, EndShiftModal } from './LiveMapModals';
import './LiveMapPage.css';

const TURKEY_CENTER = [39.0, 35.5];
const DEFAULT_ZOOM = 7;
const REFRESH_INTERVAL = 10000;
const SHOW_ALL_KEY = 'flm.showAllRoutes';
const HIDDEN_KEY = 'flm.hiddenCouriers';
/** Yan liste sürükle-bırak veri tipi: "paketId|mevcutKuryeId" (atanmamışta kurye boş). */
const DND_TYPE = 'application/x-hizliyo-delivery';

// Harita katman sırası (üstte olan önde): atanmamış > kurye > restoran > seçili teslim > tüm rotalar.
const Z = { unassigned: 7500, courier: 7000, restaurant: 6000, drop: 5000, allDrop: 3000, label: 4000 };

const ACTION_BUSY_LABEL = { start: 'Başlatılıyor…', end: 'Bitiriliyor…', pause: 'Durduruluyor…', resume: 'Açılıyor…' };

const readJson = (key, fallback) => {
  try { return JSON.parse(window.localStorage.getItem(key) || 'null') ?? fallback; } catch { return fallback; }
};
const writeJson = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* erişim yok: yalnız bu oturum */ }
};

const hasPoint = (lat, lng) => typeof lat === 'number' && typeof lng === 'number' && !(lat === 0 && lng === 0);
const EMPTY_BOARD = { couriers: [], offShift: [], unassigned: [] };
const normalizeBoard = (data) => ({
  couriers: (data?.couriers || []).map((c) => ({ ...c, orders: c.orders || [] })),
  offShift: data?.offShift || [],
  unassigned: data?.unassigned || [],
});

const pointerOf = (ev) => {
  const p = ev?.changedTouches?.[0] ?? ev?.touches?.[0] ?? ev;
  return typeof p?.clientX === 'number' && typeof p?.clientY === 'number' ? { x: p.clientX, y: p.clientY } : null;
};

/**
 * Canlı kurye takibi — restoran panelindeki ekranın filo karşılığı. Mesaideki kuryeler, taşıdıkları
 * paketler ve kuryesiz bekleyen paketler tek ekranda; paketi kuryeye sürükleyip bırakarak atama,
 * kuryeden geri alma, kurye adına yola çıkarma, mesai başlat/bitir ve paket alımını durdurma.
 *
 * Bölge sorumlusu yalnız kendi bölgesinin kuryelerini ve paketlerini görür: süzme sunucuda yapılır,
 * bu ekran gelen veriyi olduğu gibi gösterir. Düğmeler ayrıca yetkiye göre gizlenir.
 */
const LiveMapPage = ({ accessCtx }) => {
  const canAssign = can(accessCtx, FLEET_PERMISSION.DeliveryAssign);
  const canManage = can(accessCtx, FLEET_PERMISSION.CourierManage);

  const [board, setBoard] = useState(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedOrderIdx, setSelectedOrderIdx] = useState(null);
  const [openUnassignedId, setOpenUnassignedId] = useState(null);
  const [courierColors, setCourierColors] = useState(() => sanitizeCourierColors(readJson(COLOR_STORAGE_KEY, {})));
  const [showAllRoutes, setShowAllRoutes] = useState(() => readJson(SHOW_ALL_KEY, false) === true);
  const [hiddenIds, setHiddenIds] = useState(() => {
    const raw = readJson(HIDDEN_KEY, []);
    return new Set(Array.isArray(raw) ? raw.filter((x) => typeof x === 'string') : []);
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [routeTick, setRouteTick] = useState(0);   // seçili kuryenin rotaları çizildi: vurgu yeniden uygulanır

  const { couriers, offShift, unassigned } = board;

  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const courierMarkersRef = useRef(new Map());
  const unassignedMarkersRef = useRef(new Map());
  const selLayerRef = useRef(null);
  const routesRef = useRef([]);           // seçili kuryenin rotaları, paket sırasıyla: { line, label, dashed }
  const allRoutesRef = useRef(new Map()); // "tüm rotalar": kurye → { sig, token, group }
  const allRoutesTokenRef = useRef(0);
  const firstFitRef = useRef(false);
  const boardRef = useRef(board);
  boardRef.current = board;
  const hiddenRef = useRef(hiddenIds);
  hiddenRef.current = hiddenIds;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const selectedCourier = useMemo(() => couriers.find((c) => c.courierId === selectedId) || null, [couriers, selectedId]);
  const openUnassigned = useMemo(
    () => unassigned.find((o) => o.deliveryId === openUnassignedId) || null,
    [unassigned, openUnassignedId],
  );
  const selSig = selectedCourier ? ordersSignature(selectedCourier) : '';

  // ── Veri ──────────────────────────────────────────────────────────────────────────────
  // Sürükleme sürerken veri UYGULANMAZ: yeni veri pinleri yeniden çizer, eldeki pin silinir ve
  // sürükleme haritayı kaydırmaya döner. Bırakınca bekleyen tazeleme hemen koşar.
  const draggingRef = useRef(false);
  const pendingLoadRef = useRef(false);

  const load = useCallback(async () => {
    if (draggingRef.current) { pendingLoadRef.current = true; return; }
    let res = null;
    try { res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.LIVE_BOARD); } catch { res = null; }
    if (draggingRef.current) { pendingLoadRef.current = true; return; }
    if (res?.success && res.data) { setBoard(normalizeBoard(res.data)); setError(null); }
    else setError(res?.error || res?.message || 'Kurye bilgileri alınamadı. Yenile düğmesiyle tekrar deneyin.');
    setLoading(false);
  }, []);

  const releaseDrag = useCallback(() => {
    draggingRef.current = false;
    if (pendingLoadRef.current) { pendingLoadRef.current = false; load(); }
  }, [load]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const handler = () => load();
    const events = [FLEET_HUB_METHODS.RECEIVE_COURIER_LOCATION_UPDATE, FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION];
    events.forEach((e) => window.addEventListener(`fleet:${e}`, handler));
    return () => events.forEach((e) => window.removeEventListener(`fleet:${e}`, handler));
  }, [load]);

  const afterChange = useCallback(async () => {
    try { await load(); } catch { /* sıradaki tazeleme telafi eder */ }
    emitFleetAppEvent(FLEET_APP_EVENTS.DELIVERIES_CHANGED);
  }, [load]);

  // ── Kurye rengi ───────────────────────────────────────────────────────────────────────
  // Kurye ilk görüldüğünde mesaidekilerde en az kullanılan rengi alır ve tarayıcıda hatırlanır.
  useEffect(() => {
    if (couriers.length === 0) return;
    const next = assignCourierColors(courierColors, couriers.map((c) => c.courierId));
    if (next === courierColors) return;
    setCourierColors(next);
    writeJson(COLOR_STORAGE_KEY, next);
  }, [couriers, courierColors]);
  const colorOf = useCallback((courierId) => courierColors[courierId] || COURIER_COLORS[0], [courierColors]);

  // ── Görünüm tercihleri ────────────────────────────────────────────────────────────────
  const toggleShowAllRoutes = (on) => { setShowAllRoutes(on); writeJson(SHOW_ALL_KEY, on); };

  // Haritada gizle: SALT GÖRÜNÜM. Kurye yalnız haritadan kalkar; mesai, paket alımı, atama etkilenmez.
  const setHidden = useCallback((next) => { setHiddenIds(next); writeJson(HIDDEN_KEY, [...next]); }, []);
  const toggleHidden = (courierId) => {
    const next = new Set(hiddenRef.current);
    if (next.has(courierId)) next.delete(courierId); else next.add(courierId);
    setHidden(next);
  };
  const hiddenOnShiftCount = couriers.filter((c) => hiddenIds.has(c.courierId)).length;

  // ── Tam ekran ─────────────────────────────────────────────────────────────────────────
  // Yalnız paneli değil SAYFAYI tam ekrana alırız: yerel tam ekran yalnız o öğenin içini çizer,
  // panel tam ekran olsaydı bildirimler ve pencereler görünmezdi.
  useEffect(() => {
    const sync = () => { if (!document.fullscreenElement) setIsFullscreen(false); };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !document.fullscreenElement) setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);
  useEffect(() => () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => { }); }, []);
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [isFullscreen]);
  const toggleFullscreen = async () => {
    if (isFullscreen) {
      setIsFullscreen(false);
      if (document.fullscreenElement) await document.exitFullscreen().catch(() => { });
      return;
    }
    setIsFullscreen(true);
    try { await document.documentElement.requestFullscreen?.(); } catch { /* izin yok: yalnız kaplama */ }
  };

  // ── Seçim ─────────────────────────────────────────────────────────────────────────────
  const closeCards = useCallback(() => {
    setSelectedId(null);
    setSelectedOrderIdx(null);
    setOpenUnassignedId(null);
  }, []);

  const selectCourier = useCallback((courierId) => {
    setOpenUnassignedId(null);
    setSelectedOrderIdx(null);
    setSelectedId(courierId);
  }, []);

  const focusCourier = (c) => {
    if (hiddenIds.has(c.courierId)) {
      fleetToast.info(`${c.courierName} haritada gizli. Satırdaki göz düğmesiyle tekrar gösterebilirsiniz.`);
      return;
    }
    selectCourier(c.courierId);
    if (hasPoint(c.latitude, c.longitude)) mapRef.current?.setView([c.latitude, c.longitude], 15, { animate: true });
  };

  // Paket seçildi: rotası öne çıkar, harita teslim noktasına gider. Aynı pakete ikinci tık seçimi kaldırır.
  const selectOrder = useCallback((idx) => {
    setSelectedOrderIdx((cur) => (cur === idx ? null : idx));
    const c = boardRef.current.couriers.find((x) => x.courierId === selectedIdRef.current);
    const o = c?.orders[idx];
    if (o && hasPoint(o.dropoffLat, o.dropoffLng)) mapRef.current?.panTo([o.dropoffLat, o.dropoffLng]);
  }, []);

  const focusOrder = (c, idx) => {
    const o = c.orders[idx];
    if (!o) return;
    if (hiddenIds.has(c.courierId) || !hasPoint(o.dropoffLat, o.dropoffLng)) { focusCourier(c); return; }
    setOpenUnassignedId(null);
    setSelectedId(c.courierId);
    setSelectedOrderIdx(idx);
    mapRef.current?.setView([o.dropoffLat, o.dropoffLng], 15, { animate: true });
  };

  const focusUnassigned = (o) => {
    setSelectedId(null);
    setSelectedOrderIdx(null);
    setOpenUnassignedId(o.deliveryId);
    if (hasPoint(o.dropoffLat, o.dropoffLng)) mapRef.current?.setView([o.dropoffLat, o.dropoffLng], 15, { animate: true });
  };

  // Seçili kurye mesaiden çıktıysa / gizlendiyse, açık paket atandıysa kart kapanır.
  useEffect(() => {
    if (selectedId && (!selectedCourier || hiddenIds.has(selectedId))) { setSelectedId(null); setSelectedOrderIdx(null); }
    else if (selectedCourier && selectedOrderIdx != null && selectedOrderIdx >= selectedCourier.orders.length) setSelectedOrderIdx(null);
    if (openUnassignedId && !openUnassigned) setOpenUnassignedId(null);
  }, [selectedId, selectedCourier, hiddenIds, selectedOrderIdx, openUnassignedId, openUnassigned]);

  // ── Kurye ata / başka kuryeye ver ─────────────────────────────────────────────────────
  const [transferTarget, setTransferTarget] = useState(null);

  const requestTransfer = useCallback((deliveryId, initialCourierId) => {
    const b = boardRef.current;
    for (const c of b.couriers) {
      const o = c.orders.find((x) => x.deliveryId === deliveryId);
      if (o) {
        setTransferTarget({ deliveryId, currentCourier: { id: c.courierId, name: c.courierName }, initialCourierId, subtitle: packageTitle(o) });
        return;
      }
    }
    const u = b.unassigned.find((x) => x.deliveryId === deliveryId);
    if (u) setTransferTarget({ deliveryId, currentCourier: null, initialCourierId, subtitle: packageTitle(u) });
  }, []);

  // ── Onaylı paket işlemi: kuryeden al / kurye adına yola çıkar ─────────────────────────
  const [confirm, setConfirm] = useState(null); // { kind: 'unassign' | 'onway', deliveryIds, courierName, subtitle }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const requestUnassign = useCallback((deliveryId) => {
    for (const c of boardRef.current.couriers) {
      const o = c.orders.find((x) => x.deliveryId === deliveryId);
      if (!o) continue;
      if (!isAtRestaurant(o)) {
        fleetToast.info(`${c.courierName} bu paketi teslim almış. Başka kuryeye vermek için paketi o kuryenin üstüne sürükleyin.`);
        return;
      }
      setConfirm({ kind: 'unassign', deliveryIds: [deliveryId], courierName: c.courierName || 'Kurye', subtitle: packageTitle(o) });
      return;
    }
  }, []);

  const askOnTheWay = (courier, orders) =>
    setConfirm({
      kind: 'onway',
      deliveryIds: orders.map((o) => o.deliveryId),
      courierName: courier.courierName || 'Kurye',
      subtitle: orders.length === 1 ? packageTitle(orders[0]) : `${orders.length} paket: ${orders.map(receiptLabel).join(', ')}`,
    });

  const runConfirm = async () => {
    const t = confirm;
    if (!t || confirmBusy) return;
    setConfirmBusy(true);
    try {
      const endpoint = t.kind === 'unassign'
        ? API_ENDPOINTS.FLEET_PANEL.DELIVERY_UNASSIGN_COURIER
        : API_ENDPOINTS.FLEET_PANEL.DELIVERY_MARK_ON_THE_WAY;
      // Sırayla: her paket ayrı bildirilir; biri reddedilirse diğerleri yine denenir.
      let ok = 0;
      let lastError = null;
      for (const id of t.deliveryIds) {
        // eslint-disable-next-line no-await-in-loop
        const res = await apiService.post(endpoint(id), {});
        if (res?.success) ok += 1;
        else lastError = res?.error || res?.message || null;
      }
      const failed = t.deliveryIds.length - ok;
      if (failed === 0) {
        setConfirm(null);
        fleetToast.success(t.kind === 'unassign'
          ? `Paket ${t.courierName} üzerinden alındı, atanmamış paketlerde bekliyor`
          : `${ok === 1 ? 'Paket' : `${ok} paket`} ${t.courierName} adına yola çıkarıldı`);
      } else {
        if (ok > 0) setConfirm(null);
        fleetToast.error(ok > 0
          ? `${ok} paket yola çıkarıldı, ${failed} paket çıkarılamadı: ${lastError || 'Tekrar deneyin'}`
          : (lastError || 'İşlem yapılamadı, tekrar deneyin'));
      }
      if (ok > 0) await afterChange();
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Kuryeden konum iste ───────────────────────────────────────────────────────────────
  // Haritadaki nokta kuryenin SON bildirdiği konumdur; uygulama kapalıysa eski yerde kalır.
  const locationRequestedAtRef = useRef(new Map());
  const [locationBusy, setLocationBusy] = useState(null);
  const [, setLocationTick] = useState(0);
  const requestLocation = async (courier) => {
    if (locationBusy || locationRequestCoolingDown(locationRequestedAtRef.current.get(courier.courierId), Date.now())) return;
    setLocationBusy(courier.courierId);
    try {
      const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.COURIER_LOCATION_REQUEST(courier.courierId), {});
      if (res?.success) {
        locationRequestedAtRef.current.set(courier.courierId, Date.now());
        fleetToast.success(`${courier.courierName || 'Kurye'} adlı kuryeye bildirim gönderildi. Uygulamayı açtığında konumu yenilenir.`);
      } else {
        fleetToast.error(res?.error || res?.message || 'Bildirim gönderilemedi, tekrar deneyin');
      }
    } finally {
      setLocationBusy(null);
      setLocationTick((n) => n + 1);
    }
  };

  // ── Mesai başlat / bitir, paket alımını durdur ────────────────────────────────────────
  const [courierAction, setCourierAction] = useState(null); // { courierId, kind }
  const actionBusyId = courierAction?.courierId ?? null;
  const [endShiftTarget, setEndShiftTarget] = useState(null);

  const startShift = async (r) => {
    if (actionBusyId) return;
    setCourierAction({ courierId: r.courierId, kind: 'start' });
    try {
      const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.COURIER_SHIFT_START(r.courierId), {});
      if (res?.success) {
        await load();
        fleetToast.success(`${r.courierName} artık mesaide ve paket alabilir`);
      } else {
        fleetToast.error(res?.error || res?.message || 'Mesai başlatılamadı, tekrar deneyin');
      }
    } finally {
      setCourierAction(null);
    }
  };

  const confirmEndShift = async (manualBody) => {
    const c = endShiftTarget;
    if (!c || actionBusyId) return;
    setCourierAction({ courierId: c.courierId, kind: 'end' });
    try {
      const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.COURIER_SHIFT_END(c.courierId), manualBody || {});
      if (res?.success) {
        const d = res.data || {};
        fleetToast.success(`${c.courierName} mesaiyi bitirdi: ${formatDuration(d.durationMinutes)}, bu vardiyada ${d.deliveredCount ?? 0} paket teslim etti`);
        setEndShiftTarget(null);
        if (selectedIdRef.current === c.courierId) closeCards();
        await load();
      } else {
        // Kuryede paket varsa sunucu adet vererek reddeder; pencere açık kalır.
        fleetToast.error(res?.error || res?.message || 'Mesai bitirilemedi, tekrar deneyin');
      }
    } finally {
      setCourierAction(null);
    }
  };

  const toggleDispatchPause = async (c) => {
    if (actionBusyId) return;
    const pause = !c.dispatchPaused;
    setCourierAction({ courierId: c.courierId, kind: pause ? 'pause' : 'resume' });
    try {
      const res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.COURIER_DISPATCH_PAUSE(c.courierId), { paused: pause });
      if (res?.success) {
        fleetToast.success(pause
          ? `${c.courierName} yeni paket almayacak. Üzerindeki paketler etkilenmez; yeni mesaide kendiliğinden açılır.`
          : `${c.courierName} yeniden paket alabilir`);
        await load();
      } else {
        fleetToast.error(res?.error || res?.message || 'İşlem yapılamadı, tekrar deneyin');
      }
    } finally {
      setCourierAction(null);
    }
  };

  // ── Sürükle-bırak ─────────────────────────────────────────────────────────────────────
  // Kuryedeki ve henüz teslim alınmamış paket sürüklenirken "atanmamışa bırak" hedefleri görünür.
  const [ownedDrag, setOwnedDrag] = useState(null); // null | 'list' | 'map'
  const [unassignZoneHover, setUnassignZoneHover] = useState(false);
  const [listDropTarget, setListDropTarget] = useState(null);
  const mapUnassignZoneRef = useRef(null);
  const dropHighlightRef = useRef(null);
  const lastPointerRef = useRef(null);

  const isOverMapUnassignZone = (pt) => {
    const rect = mapUnassignZoneRef.current?.getBoundingClientRect();
    return !!rect && !!pt && pt.x >= rect.left && pt.x <= rect.right && pt.y >= rect.top && pt.y <= rect.bottom;
  };

  const endListDrag = () => {
    setListDropTarget(null);
    setOwnedDrag(null);
    setUnassignZoneHover(false);
    releaseDrag();
  };

  // Bırakılacak kuryeyi sürükleme SIRASINDA göster: kalabalıkta neyin üstüne bırakıldığı tahmin edilmesin.
  const setDropHighlight = (courierId) => {
    if (dropHighlightRef.current === courierId) return;
    const prev = dropHighlightRef.current ? courierMarkersRef.current.get(dropHighlightRef.current) : null;
    prev?.getElement()?.classList.remove('lm-drop-target');
    dropHighlightRef.current = courierId;
    const next = courierId ? courierMarkersRef.current.get(courierId) : null;
    next?.getElement()?.classList.add('lm-drop-target');
  };

  const nearestCourierAt = (latlng) => {
    const map = mapRef.current;
    if (!map || !latlng) return null;
    const pt = map.latLngToContainerPoint(latlng);
    const points = boardRef.current.couriers
      .filter((c) => hasPoint(c.latitude, c.longitude) && !hiddenRef.current.has(c.courierId))
      .map((c) => {
        const p = map.latLngToContainerPoint([c.latitude, c.longitude]);
        return { id: c.courierId, x: p.x, y: p.y };
      });
    return nearestWithin(pt, points);
  };

  // Harita işaretlerinin çağırdığı işlevler bir referansta durur: işaretler yeniden kurulmadan güncel kalır.
  const actionsRef = useRef({});
  actionsRef.current = { selectCourier, selectOrder, requestTransfer, requestUnassign, nearestCourierAt, setDropHighlight, isOverMapUnassignZone, releaseDrag, focusUnassigned };

  /**
   * Paket pinine sağ tık + sürükle-bırak. Pin paketin TESLİMAT ADRESİDİR: bırakınca her durumda
   * yerine döner (adres değişmez), yalnız hedef kurye okunur.
   */
  const attachAssignGestures = useCallback((marker, order, ownerCourierId) => {
    if (!canAssign || !order.deliveryId) return;
    const deliveryId = order.deliveryId;
    const owned = !!ownerCourierId && isAtRestaurant(order);
    marker.dragging?.enable();
    marker.on('contextmenu', () => actionsRef.current.requestTransfer(deliveryId, null));
    marker.on('dragstart', () => {
      draggingRef.current = true;
      marker.__home = marker.getLatLng();
      lastPointerRef.current = null;
      if (owned) setOwnedDrag('map');
    });
    marker.on('drag', (e) => {
      const a = actionsRef.current;
      lastPointerRef.current = pointerOf(e.originalEvent) || lastPointerRef.current;
      const overZone = owned && a.isOverMapUnassignZone(lastPointerRef.current);
      setUnassignZoneHover(overZone);
      const target = overZone ? null : a.nearestCourierAt(marker.getLatLng());
      a.setDropHighlight(target && target !== ownerCourierId ? target : null);
    });
    marker.on('dragend', () => {
      const a = actionsRef.current;
      const dropLatLng = marker.getLatLng();
      const droppedOnUnassign = owned && a.isOverMapUnassignZone(lastPointerRef.current);
      const target = droppedOnUnassign ? null : a.nearestCourierAt(dropLatLng);
      if (marker.__home) marker.setLatLng(marker.__home);
      a.setDropHighlight(null);
      setOwnedDrag(null);
      setUnassignZoneHover(false);
      a.releaseDrag();
      if (droppedOnUnassign) { a.requestUnassign(deliveryId); return; }
      if (!target) { fleetToast.info('Paketi bir kuryenin üstüne bırakın ya da pakete sağ tıklayın.'); return; }
      if (target === ownerCourierId) return;   // kendi kuryesine bırakıldı: işlem yok
      a.requestTransfer(deliveryId, target);
    });
  }, [canAssign]);

  // ── Harita ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return undefined;
    const map = L.map(mapElRef.current, { center: TURKEY_CENTER, zoom: DEFAULT_ZOOM, zoomControl: true, attributionControl: false });
    addGoogleBaseLayer(map, { dark: true });
    mapRef.current = map;
    selLayerRef.current = L.layerGroup().addTo(map);
    setMapReady(true);
    const courierMarkers = courierMarkersRef.current;
    const unassignedMarkers = unassignedMarkersRef.current;
    const allRoutes = allRoutesRef.current;
    return () => {
      map.remove();
      mapRef.current = null;
      selLayerRef.current = null;
      courierMarkers.clear();
      unassignedMarkers.clear();
      allRoutes.clear();
      routesRef.current = [];
    };
  }, []);

  const fitOnce = (latlngs) => {
    const map = mapRef.current;
    if (!map || firstFitRef.current || latlngs.length === 0) return;
    if (latlngs.length === 1) map.setView(latlngs[0], 15);
    else map.fitBounds(latlngs, { padding: [60, 60], maxZoom: 15 });
    firstFitRef.current = true;
  };

  // Kurye işaretleri: ekle / güncelle / sil (titremesiz). Gizlenen kurye çizilmez.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const markers = courierMarkersRef.current;
    const seen = new Set();
    const withLoc = couriers.filter((c) => hasPoint(c.latitude, c.longitude) && !hiddenIds.has(c.courierId));

    withLoc.forEach((c) => {
      seen.add(c.courierId);
      const fr = freshness(c.lastHeartbeatUtc);
      const spec = {
        name: courierDisplayName(c.courierName), color: colorOf(c.courierId), freshColor: fr.color,
        count: c.orders.length, paused: !!c.dispatchPaused,
      };
      const key = Object.values(spec).join('|');
      const title = c.dispatchPaused ? `${c.courierName}, paket almıyor` : c.courierName;
      let marker = markers.get(c.courierId);
      if (!marker) {
        marker = L.marker([c.latitude, c.longitude], {
          icon: courierIcon(spec), zIndexOffset: Z.courier, riseOnHover: true, riseOffset: 10000, title, keyboard: false,
        }).addTo(map);
        marker.on('click', () => actionsRef.current.selectCourier(c.courierId));
        marker.__key = key;
        markers.set(c.courierId, marker);
      } else {
        marker.setLatLng([c.latitude, c.longitude]);
        if (marker.__key !== key) { marker.setIcon(courierIcon(spec)); marker.__key = key; }
        const el = marker.getElement();
        if (el) el.title = title;
      }
    });

    markers.forEach((m, id) => { if (!seen.has(id)) { map.removeLayer(m); markers.delete(id); } });
    fitOnce(withLoc.map((c) => [c.latitude, c.longitude]));
  }, [couriers, hiddenIds, colorOf, mapReady]);

  // Atanmamış paketler: teslim noktasında kare "!" işareti (her zaman görünür).
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const markers = unassignedMarkersRef.current;
    const seen = new Set();
    const withLoc = unassigned.filter((o) => o.deliveryId && hasPoint(o.dropoffLat, o.dropoffLng));

    withLoc.forEach((o) => {
      seen.add(o.deliveryId);
      const label = [receiptLabel(o), shortCustomerName(o.customerName)].filter(Boolean).join(', ');
      const title = `Kurye atanmadı, ${packageTitle(o)}, ${o.statusText}`;
      const existing = markers.get(o.deliveryId);
      if (existing) {
        if (!draggingRef.current) existing.setLatLng([o.dropoffLat, o.dropoffLng]);
        // Fiş no / müşteri adı sonradan dolabiliyor: işaret de tazelenir.
        if (existing.__label !== label) { existing.setIcon(unassignedIcon(label)); existing.__label = label; }
        return;
      }
      const m = L.marker([o.dropoffLat, o.dropoffLng], {
        icon: unassignedIcon(label), zIndexOffset: Z.unassigned, riseOnHover: true, riseOffset: 10000, title, keyboard: false,
      }).addTo(map);
      m.__label = label;
      m.on('click', () => {
        const fresh = boardRef.current.unassigned.find((x) => x.deliveryId === o.deliveryId);
        if (fresh) actionsRef.current.focusUnassigned(fresh);
      });
      attachAssignGestures(m, o, null);
      markers.set(o.deliveryId, m);
    });

    markers.forEach((m, id) => { if (!seen.has(id)) { map.removeLayer(m); markers.delete(id); } });
    // Mesaide konumlu kurye yoksa ilk açılışta atanmamış paketlere odaklan.
    fitOnce(withLoc.map((o) => [o.dropoffLat, o.dropoffLng]));
  }, [unassigned, mapReady, attachAssignGestures]);

  /** Bir kuryenin teslim pinleri (ve restoranda bekleyen paketlerinin restoranları) verilen katmana çizilir. */
  const drawPins = useCallback((layer, c, col, zBase, onPinClick) => {
    const restaurants = new Set();
    c.orders.forEach((o, i) => {
      if (isAtRestaurant(o) && hasPoint(o.pickupLat, o.pickupLng)) {
        const rk = `${o.pickupLat.toFixed(5)},${o.pickupLng.toFixed(5)}`;
        if (!restaurants.has(rk)) {
          restaurants.add(rk);
          L.marker([o.pickupLat, o.pickupLng], {
            icon: restaurantIcon(), zIndexOffset: Z.restaurant, keyboard: false,
            title: `${o.partnerName || 'Restoran'}, paket burada bekliyor`,
          }).addTo(layer);
        }
      }
      if (!hasPoint(o.dropoffLat, o.dropoffLng)) return;
      const pin = L.marker([o.dropoffLat, o.dropoffLng], {
        icon: dropoffIcon({ color: col, num: i + 1, label: receiptLabel(o), atRestaurant: isAtRestaurant(o) }),
        zIndexOffset: zBase + i, riseOnHover: true, riseOffset: 10000, keyboard: false,
        title: `${c.courierName}, ${packageTitle(o)}, ${o.statusText}`,
      }).addTo(layer);
      pin.on('click', () => onPinClick(i));
      attachAssignGestures(pin, o, c.courierId);
    });
  }, [attachAssignGestures]);

  // Seçili kurye: teslim pinleri + yol rotaları (mesafe, süre, varış etiketiyle). Paket imzası
  // değişmedikçe yeniden çizim/istek yok — 10 sn'lik tazelemede sabit kalır.
  useEffect(() => {
    const layer = selLayerRef.current;
    if (!mapReady || !layer) return undefined;
    layer.clearLayers();
    routesRef.current = [];
    const c = boardRef.current.couriers.find((x) => x.courierId === selectedId);
    if (!c) return undefined;
    const col = colorOf(c.courierId);
    drawPins(layer, c, col, Z.drop, (i) => actionsRef.current.selectOrder(i));
    if (!hasPoint(c.latitude, c.longitude) || c.orders.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      let res = null;
      try { res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COURIER_ROUTES(c.courierId)); } catch { res = null; }
      // Yanıt beklenirken başka kuryeye geçildiyse / paketler değiştiyse bu rotalar artık geçersiz.
      if (cancelled || !res?.success || !Array.isArray(res.data)) return;
      const byOrderNo = new Map(res.data.map((r) => [r.orderNo, r]));
      c.orders.forEach((o, i) => {
        const route = o.orderNo ? byOrderNo.get(o.orderNo) : null;
        if (!route || !Array.isArray(route.path) || route.path.length < 2) return;
        const path = route.path.map((p) => [p.lat, p.lng]);
        const line = L.polyline(path, { color: col, weight: 5, opacity: 0.85, lineCap: 'round' }).addTo(layer);
        line.on('click', () => actionsRef.current.selectOrder(i));
        const text = formatRouteLabel({
          distanceMeters: route.distanceMeters, durationSeconds: route.durationSeconds, isFallback: route.isFallback, now: Date.now(),
        });
        const label = L.marker(path[Math.floor(path.length / 2)], {
          icon: routeLabelIcon(text, col), zIndexOffset: Z.label, keyboard: false, title: text,
        }).addTo(layer);
        label.on('click', () => actionsRef.current.selectOrder(i));
        routesRef.current[i] = { line, label, dashed: !!route.viaPickup };
      });
      setRouteTick((n) => n + 1);
    })();
    return () => { cancelled = true; };
  }, [selectedId, selSig, mapReady, colorOf, drawPins]);

  // Rotalar kuryenin rengindedir; seçili paket kalınlık ve akan çizgiyle öne çıkar, diğerleri söner.
  // Paket henüz restorandaysa rota kesiklidir (kurye önce restorana gidecek).
  useEffect(() => {
    routesRef.current.forEach((r, i) => {
      if (!r) return;
      const isSel = selectedOrderIdx == null || selectedOrderIdx === i;
      const focused = selectedOrderIdx === i;
      r.line.setStyle({
        weight: focused ? 6 : (isSel ? 5 : 3),
        opacity: isSel ? (r.dashed ? 0.9 : 0.85) : 0.3,
        dashArray: focused ? '2 12' : (r.dashed ? '6 9' : null),
      });
      r.line.getElement()?.classList.toggle('lm-route-flow', focused);
      if (focused) r.line.bringToFront();
      r.label.setOpacity(isSel ? 1 : 0.35);
      r.label.setZIndexOffset(isSel ? Z.label + 100 : Z.label);
    });
  }, [selectedOrderIdx, routeTick]);

  // "Tüm rotaları göster": paket taşıyan her kuryenin rotası ve teslim pinleri sürekli çizili.
  // Seçili kurye hariç tutulur: onun etkileşimli rotası zaten yukarıda çizili.
  useEffect(() => {
    const map = mapRef.current;
    const entries = allRoutesRef.current;
    const clear = (e) => { map?.removeLayer(e.group); };
    if (!showAllRoutes || !mapReady || !map) {
      entries.forEach(clear);
      entries.clear();
      return;
    }
    const wanted = new Map(
      couriers
        .filter((c) => c.courierId !== selectedId && !hiddenIds.has(c.courierId) && c.orders.length > 0 && hasPoint(c.latitude, c.longitude))
        .map((c) => [c.courierId, c]),
    );
    entries.forEach((e, id) => { if (!wanted.has(id)) { clear(e); entries.delete(id); } });

    wanted.forEach((c, id) => {
      const col = colorOf(id);
      const sig = `${ordersSignature(c)}#${col}`;
      const existing = entries.get(id);
      if (existing?.sig === sig) return;
      if (existing) clear(existing);

      const group = L.layerGroup().addTo(map);
      const entry = { sig, token: ++allRoutesTokenRef.current, group };
      entries.set(id, entry);
      drawPins(group, c, col, Z.allDrop, () => actionsRef.current.selectCourier(id));

      apiService.get(API_ENDPOINTS.FLEET_PANEL.COURIER_ROUTES(id)).then((res) => {
        // Yanıt beklenirken tik kapandıysa / paketler değiştiyse / kurye seçildiyse çizme.
        if (entries.get(id)?.token !== entry.token || !res?.success || !Array.isArray(res.data)) return;
        res.data.forEach((route) => {
          if (!Array.isArray(route.path) || route.path.length < 2) return;
          const line = L.polyline(route.path.map((p) => [p.lat, p.lng]), {
            color: col, weight: 4, opacity: 0.7, lineCap: 'round', dashArray: route.viaPickup ? '6 9' : null,
          }).addTo(group);
          line.on('click', () => actionsRef.current.selectCourier(id));
        });
      }).catch(() => { /* rota gelmezse pinler yine durur */ });
    });
  }, [showAllRoutes, couriers, selectedId, hiddenIds, mapReady, colorOf, drawPins]);

  // ── Liste ─────────────────────────────────────────────────────────────────────────────
  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const matches = (name) => !term || String(name || '').toLocaleLowerCase('tr').includes(term);
  const shownCouriers = couriers.filter((c) => matches(c.courierName));
  const shownOffShift = offShift.filter((r) => matches(r.courierName));
  const totalPackages = couriers.reduce((n, c) => n + c.orders.length, 0);

  const selWaiting = waitingAtRestaurant(selectedCourier);
  const selCooling = selectedCourier
    ? locationRequestCoolingDown(locationRequestedAtRef.current.get(selectedCourier.courierId), Date.now())
    : false;
  const selLocBusy = !!selectedCourier && locationBusy === selectedCourier.courierId;
  const selAction = selectedCourier && courierAction?.courierId === selectedCourier.courierId ? courierAction.kind : null;
  const selFresh = selectedCourier ? freshness(selectedCourier.lastHeartbeatUtc) : null;

  const fallbackCouriers = useMemo(
    () => [...couriers.map((c) => ({ courierId: c.courierId, courierName: c.courierName })),
      ...offShift.map((r) => ({ courierId: r.courierId, courierName: r.courierName, offShift: true }))],
    [couriers, offShift],
  );

  const dragProps = (o, ownerId) => (canAssign && o.deliveryId ? {
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.setData(DND_TYPE, `${o.deliveryId}|${ownerId || ''}`);
      e.dataTransfer.effectAllowed = 'move';
      draggingRef.current = true;
      // Bir sonraki tura ertelenir: sürükleme başlarken liste yeniden dizilirse tarayıcı sürüklemeyi iptal eder.
      if (ownerId && isAtRestaurant(o)) setTimeout(() => { if (draggingRef.current) setOwnedDrag('list'); }, 0);
    },
    onDragEnd: endListDrag,
    onContextMenu: (e) => { e.preventDefault(); requestTransfer(o.deliveryId, null); },
  } : {});

  return (
    <div className={`lm-container${isFullscreen ? ' lm-fullscreen' : ''}`}>
      <div className="lm-topbar">
        <div className="lm-title">
          <h1>Canlı kurye takibi</h1>
          <p>
            Kuryeler, taşıdıkları ve atanmamış paketler. 10 saniyede bir yenilenir.
            {canAssign && ' Kurye atamak için paketi kuryenin üstüne sürükleyin ya da sağ tıklayın; kuryeden almak için atanmamışlara sürükleyin.'}
          </p>
        </div>
        <label className={`lm-toggle${showAllRoutes ? ' on' : ''}`} title="Paket taşıyan tüm kuryelerin rotalarını haritada sürekli göster">
          <input type="checkbox" checked={showAllRoutes} onChange={(e) => toggleShowAllRoutes(e.target.checked)} />
          Tüm rotaları göster
        </label>
        <div className="lm-stats-row">
          <div className="lm-stat"><span>Kurye</span><strong>{couriers.length}</strong></div>
          <div className="lm-stat"><span>Paket</span><strong>{totalPackages}</strong></div>
          {/* Veri hiç alınamadıysa "0" DEMEZ: 0, "bekleyen paket yok" anlamına gelir ve yanıltır. */}
          <div className={`lm-stat${unassigned.length > 0 ? ' unassigned' : ''}`} title={error && board === EMPTY_BOARD ? 'Atanmamış paketler şu an alınamıyor' : undefined}>
            <span>Atanmamış</span><strong>{error && board === EMPTY_BOARD ? '—' : unassigned.length}</strong>
          </div>
        </div>
        {/* Gizli kurye unutulmasın: haritada eksik kurye varsa burada söylenir, tek tıkla geri gelir. */}
        {hiddenOnShiftCount > 0 && (
          <button className="lm-top-btn dashed" onClick={() => setHidden(new Set())} title="Gizlenen kuryeleri haritada tekrar göster">
            <EyeOff size={14} /> {hiddenOnShiftCount} kurye gizli, hepsini göster
          </button>
        )}
        <button className="lm-refresh-btn" onClick={load} title="Yenile" aria-label="Yenile">
          <RefreshCw size={16} className={loading ? 'lm-spin' : ''} />
        </button>
        <button
          className={`lm-top-btn${isFullscreen ? ' on' : ''}`}
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Tam ekrandan çık (Esc)' : 'Tam ekran'}
          aria-label={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
        >
          {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          <span className="lm-hide-narrow">{isFullscreen ? 'Küçült' : 'Tam ekran'}</span>
        </button>
      </div>

      <div className="lm-body">
        <aside className="lm-sidebar">
          <div className="lm-search-wrapper">
            <Search size={14} className="lm-search-icon" />
            <input
              type="text"
              placeholder="Kurye ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="lm-search-input"
              aria-label="Kurye ara"
            />
          </div>

          <div className="lm-courier-list">
            {/* Kuryedeki bir paket sürüklenirken bölüm BIRAKMA HEDEFİ olur (liste boşken de görünür):
                paket kuryeden alınır, atanmamışlara döner. Onay penceresi açılır. */}
            {(unassigned.length > 0 || ownedDrag === 'list') && (
              <section
                className={`lm-section lm-unassigned${ownedDrag === 'list' ? ' droppable' : ''}${ownedDrag === 'list' && unassignZoneHover ? ' over' : ''}`}
                onDragOver={(e) => {
                  if (ownedDrag !== 'list' || !e.dataTransfer.types.includes(DND_TYPE)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (!unassignZoneHover) setUnassignZoneHover(true);
                }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setUnassignZoneHover(false); }}
                onDrop={(e) => {
                  const [deliveryId, owner] = (e.dataTransfer.getData(DND_TYPE) || '').split('|');
                  setUnassignZoneHover(false);
                  if (!deliveryId || !owner) return;   // zaten atanmamış paket: işlem yok
                  e.preventDefault();
                  requestUnassign(deliveryId);
                }}
              >
                <h2 className="lm-section-title"><PackageOpen size={13} /> Atanmamış paketler ({unassigned.length})</h2>
                {ownedDrag === 'list' && <p className="lm-section-hint">Paketi kuryeden almak için buraya bırakın.</p>}
                {byReceiptNo(unassigned).map(({ o }) => {
                  const hasLoc = hasPoint(o.dropoffLat, o.dropoffLng);
                  const hint = waitHint(o.waitReason);
                  return (
                    <div key={o.deliveryId} className={`lm-un-row${openUnassignedId === o.deliveryId ? ' selected' : ''}`}>
                      <button
                        type="button"
                        className="lm-un-item"
                        onClick={() => focusUnassigned(o)}
                        title={canAssign ? (hasLoc ? 'Sağ tık: kurye ata' : 'Teslim noktası konumu yok. Sağ tık: kurye ata') : undefined}
                        {...dragProps(o, null)}
                      >
                        <span className="lm-un-mark" aria-hidden="true">!</span>
                        <span className="lm-un-text">
                          <span className="lm-un-name">{packageTitle(o)}</span>
                          <span className="lm-un-meta">
                            {[o.customerName, o.statusText, o.createdAtUtc ? relativeTime(o.createdAtUtc) : null].filter(Boolean).join(', ')}
                          </span>
                        </span>
                      </button>
                      {/* Neden bekliyor: uyarı değil açıklama, ikincil metin tonunda. */}
                      {hint && (
                        <p className="lm-un-hint">
                          {hint.text}
                          {hint.assign && canAssign && (
                            <> <button type="button" className="lm-link" onClick={() => requestTransfer(o.deliveryId, null)}>Kurye ata</button></>
                          )}
                        </p>
                      )}
                    </div>
                  );
                })}
              </section>
            )}

            {loading && couriers.length === 0 ? (
              <div className="lm-list-loading"><Loader2 size={22} className="lm-spin" /><span>Konumlar yükleniyor…</span></div>
            ) : couriers.length === 0 ? (
              <div className="lm-list-empty"><Bike size={22} /><span>Şu an mesaide kurye yok.</span></div>
            ) : shownCouriers.length === 0 ? (
              <div className="lm-list-empty"><span>Bu adla mesaide kurye yok.</span></div>
            ) : (
              shownCouriers.map((c) => {
                const fr = freshness(c.lastHeartbeatUtc);
                const col = colorOf(c.courierId);
                const isHidden = hiddenIds.has(c.courierId);
                const act = courierAction?.courierId === c.courierId ? courierAction.kind : null;
                const pauseBusy = act === 'pause' || act === 'resume';
                return (
                  // Liste satırı BIRAKMA HEDEFİ: kalabalık haritada ya da konumu olmayan kuryede
                  // (haritada işareti yok) atama buradan yapılır.
                  <div
                    key={c.courierId}
                    className={`lm-courier-row${selectedId === c.courierId ? ' selected' : ''}${listDropTarget === c.courierId ? ' drop' : ''}`}
                    onDragOver={(e) => {
                      if (!canAssign || !e.dataTransfer.types.includes(DND_TYPE)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (listDropTarget !== c.courierId) setListDropTarget(c.courierId);
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setListDropTarget((t) => (t === c.courierId ? null : t));
                    }}
                    onDrop={(e) => {
                      if (!canAssign) return;
                      e.preventDefault();
                      setListDropTarget(null);
                      const [deliveryId, owner] = (e.dataTransfer.getData(DND_TYPE) || '').split('|');
                      if (!deliveryId || owner === c.courierId) return;
                      requestTransfer(deliveryId, c.courierId);
                    }}
                  >
                    <div className="lm-courier-line">
                      <button className={`lm-courier-item${isHidden ? ' dim' : ''}`} onClick={() => focusCourier(c)}>
                        <span className="lm-courier-avatar" style={{ background: col }} title="Kuryenin rengi: haritadaki işareti, paketleri ve rotası da bu renkte">
                          <Bike size={14} />
                          <span className={`lm-courier-dot ${fr.cls}`} />
                          {c.orders.length > 0 && <span className="lm-courier-count">{c.orders.length}</span>}
                        </span>
                        <span className="lm-courier-info">
                          <span className="lm-courier-name">{c.courierName || 'Kurye'}</span>
                          <span className="lm-courier-meta">{fr.label}, {packageSummary(c)}</span>
                          {(c.dispatchPaused || isHidden) && (
                            <span className="lm-courier-tags">
                              {c.dispatchPaused && <span className="lm-tag dashed">Paket almıyor</span>}
                              {isHidden && <span className="lm-tag">Haritada gizli</span>}
                            </span>
                          )}
                        </span>
                      </button>
                      <div className="lm-row-actions">
                        <button
                          className={`lm-icon-btn${isHidden ? ' on' : ''}`}
                          onClick={() => toggleHidden(c.courierId)}
                          title={isHidden ? 'Haritada göster' : 'Haritada gizle (mesai ve paket alımı etkilenmez)'}
                          aria-label={isHidden ? 'Haritada göster' : 'Haritada gizle'}
                          aria-pressed={isHidden}
                        >
                          {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                        {canManage && (
                          <>
                            <button
                              className={`lm-icon-btn${c.dispatchPaused ? ' go' : ''}`}
                              onClick={() => toggleDispatchPause(c)}
                              disabled={actionBusyId === c.courierId}
                              title={c.dispatchPaused ? 'Paket almaya aç' : 'Paket alımını durdur'}
                              aria-label={c.dispatchPaused ? 'Paket almaya aç' : 'Paket alımını durdur'}
                            >
                              {pauseBusy ? <Loader2 size={13} className="lm-spin" /> : (c.dispatchPaused ? <Play size={13} /> : <Pause size={13} />)}
                            </button>
                            <button
                              className="lm-icon-btn stop"
                              onClick={() => setEndShiftTarget(c)}
                              disabled={actionBusyId === c.courierId}
                              title="Mesaiyi bitir"
                              aria-label="Mesaiyi bitir"
                            >
                              {act === 'end' ? <Loader2 size={13} className="lm-spin" /> : <Flag size={13} />}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Paket çipleri: sürükle (başka kuryeye / atanmamışlara), sağ tık (kurye değiştir). */}
                    {c.orders.length > 0 && (
                      <div className="lm-chips">
                        {byReceiptNo(c.orders).map(({ o, i }) => {
                          const atRest = isAtRestaurant(o);
                          return (
                            <span
                              key={o.deliveryId ?? `${c.courierId}-${i}`}
                              role="button"
                              tabIndex={0}
                              className={`lm-chip${atRest ? ' waiting' : ''}${selectedId === c.courierId && selectedOrderIdx === i ? ' selected' : ''}`}
                              style={{ '--chip': col }}
                              onClick={() => focusOrder(c, i)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focusOrder(c, i); } }}
                              title={`${packageTitle(o)}, ${o.statusText}. Tıkla: haritada göster${canAssign ? `. Sürükle: başka kuryeye${atRest ? ' ya da atanmamışlara' : ''}. Sağ tık: kurye değiştir` : ''}`}
                              {...dragProps(o, c.courierId)}
                            >
                              <span className="lm-chip-dot" />
                              {receiptLabel(o)}{atRest ? ', restoranda' : ''}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Mesaide olmayan kuryeler: haritada işaretleri yok, buradan mesaiye alınır. */}
            {shownOffShift.length > 0 && (
              <section className="lm-section">
                <h2 className="lm-section-title">Mesaide olmayanlar ({offShift.length})</h2>
                {shownOffShift.map((r) => (
                  <div key={r.courierId} className="lm-off-row">
                    <span className="lm-off-avatar"><Bike size={13} /></span>
                    <span className="lm-off-name">{r.courierName || 'Kurye'}</span>
                    {canManage && (
                      <button className="lm-start-btn" onClick={() => startShift(r)} disabled={actionBusyId === r.courierId}>
                        {actionBusyId === r.courierId ? <><Loader2 size={12} className="lm-spin" /> {ACTION_BUSY_LABEL.start}</> : 'Mesaiye başlat'}
                      </button>
                    )}
                  </div>
                ))}
              </section>
            )}
          </div>
        </aside>

        <div className="lm-map-area">
          <div className="lm-map" ref={mapElRef} />

          {/* Kuryedeki paketin pini sürüklenirken beliren bırakma alanı. Tıklamayı yutmaz;
              bırakma noktası ölçülerek anlaşılır. */}
          {ownedDrag === 'map' && (
            <div ref={mapUnassignZoneRef} className={`lm-unassign-zone${unassignZoneHover ? ' over' : ''}`}>
              <PackageOpen size={16} /> Kuryeden almak için buraya bırakın
            </div>
          )}
          {error && <div className="lm-map-error" role="alert">{error}</div>}

          {selectedCourier && (
            <div className="lm-detail" style={{ '--courier': colorOf(selectedCourier.courierId) }}>
              <div className="lm-detail-head">
                <span className="lm-detail-color" title="Bu kuryenin rengi: paketleri ve rotası da bu renkte" />
                <div className="lm-detail-title">
                  <strong>{selectedCourier.courierName || 'Kurye'}</strong>
                </div>
                <button className="lm-detail-close" onClick={closeCards} aria-label="Kapat"><X size={16} /></button>
              </div>

              <div className="lm-detail-sub">
                <span style={{ color: selFresh.color }}>{selFresh.label}</span>
                {selectedCourier.courierPhone && <a href={`tel:${selectedCourier.courierPhone}`}>{selectedCourier.courierPhone}</a>}
                <strong>{packageSummary(selectedCourier)}</strong>
              </div>

              {/* Haritadaki nokta kuryenin son bildirdiği konumdur; saat ona ne kadar güvenileceğini söyler. */}
              <div className="lm-detail-loc">
                <span><LocateFixed size={13} /> {lastLocationText(selectedCourier.lastHeartbeatUtc)}</span>
                <button
                  className="lm-pill"
                  onClick={() => requestLocation(selectedCourier)}
                  disabled={selLocBusy || selCooling}
                  title="Kuryeye konumunu güncellemesi için bildirim gönderir"
                >
                  {selLocBusy ? 'Gönderiliyor…' : selCooling ? 'Bildirim gönderildi' : 'Konum iste'}
                </button>
              </div>

              {selectedCourier.dispatchPaused && <p className="lm-paused-note">Yeni paket almıyor</p>}

              {canManage && (
                <div className="lm-detail-actions">
                  <button className="lm-act wide" onClick={() => toggleDispatchPause(selectedCourier)} disabled={!!selAction}>
                    {selAction === 'pause' || selAction === 'resume'
                      ? <><Loader2 size={12} className="lm-spin" /> {ACTION_BUSY_LABEL[selAction]}</>
                      : (selectedCourier.dispatchPaused ? <><Play size={12} /> Paket almaya aç</> : <><Pause size={12} /> Paket alımını durdur</>)}
                  </button>
                  <button className="lm-act wide stop" onClick={() => setEndShiftTarget(selectedCourier)} disabled={!!selAction}>
                    {selAction === 'end' ? <><Loader2 size={12} className="lm-spin" /> {ACTION_BUSY_LABEL.end}</> : <><Flag size={12} /> Mesaiyi bitir</>}
                  </button>
                </div>
              )}

              {canAssign && selWaiting.length >= 2 && (
                <button className="lm-onway-all" onClick={() => askOnTheWay(selectedCourier, selWaiting)}>
                  <Navigation size={13} /> Restorandaki {selWaiting.length} paketi yola çıkar
                </button>
              )}

              {selectedCourier.orders.length === 0 ? (
                <p className="lm-detail-empty">Şu an üzerinde paket yok.</p>
              ) : (
                <div className="lm-orders">
                  {selectedCourier.orders.map((o, i) => {
                    const waiting = isAtRestaurant(o);
                    return (
                      <div key={o.deliveryId ?? i} className={`lm-order${selectedOrderIdx === i ? ' selected' : ''}`}>
                        <button className="lm-order-main" onClick={() => selectOrder(i)} title="Haritada göster">
                          <span className="lm-order-num">{i + 1}</span>
                          <span className="lm-order-text">
                            <span className="lm-order-name">{packageTitle(o)}</span>
                            <span className={`lm-order-state ${waiting ? 'waiting' : 'onway'}`}>
                              {waiting ? 'Restoranda bekliyor' : 'Kuryenin üzerinde'}, {o.statusText}
                            </span>
                            {o.items && <span className="lm-order-line">{o.items}</span>}
                            {o.deliveryAddress && <span className="lm-order-line muted">{o.deliveryAddress}</span>}
                            {(o.customerName || o.customerPhone) && (
                              <span className="lm-order-line muted">{[o.customerName, o.customerPhone].filter(Boolean).join(', ')}</span>
                            )}
                          </span>
                        </button>
                        {canAssign && o.deliveryId && (
                          <div className="lm-order-actions">
                            <button className="lm-act" onClick={() => requestTransfer(o.deliveryId, null)} title="Paketi başka bir kuryeye verir">
                              <Repeat size={12} /> Kurye değiştir
                            </button>
                            {waiting && (
                              <>
                                <button className="lm-act go" onClick={() => askOnTheWay(selectedCourier, [o])} title="Kurye adına paketi teslim alındı ve yolda olarak işaretler">
                                  <Navigation size={12} /> Yola çıkar
                                </button>
                                <button className="lm-act" onClick={() => requestUnassign(o.deliveryId)} title="Paketi bu kuryeden alıp atanmamışlara geri koyar">
                                  <Undo2 size={12} /> Kuryeden al
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {openUnassigned && !selectedCourier && (
            <div className="lm-detail" style={{ '--courier': UNASSIGNED_COLOR }}>
              <div className="lm-detail-head">
                <div className="lm-detail-title">
                  <span className="lm-un-tag">Kurye atanmadı</span>
                  <strong>{packageTitle(openUnassigned)}</strong>
                </div>
                <button className="lm-detail-close" onClick={closeCards} aria-label="Kapat"><X size={16} /></button>
              </div>
              <p className="lm-detail-line">
                {[openUnassigned.statusText, openUnassigned.createdAtUtc ? `${relativeTime(openUnassigned.createdAtUtc)} açıldı` : null].filter(Boolean).join(', ')}
              </p>
              {waitHint(openUnassigned.waitReason) && <p className="lm-detail-line muted">{waitHint(openUnassigned.waitReason).text}</p>}
              {openUnassigned.items && <p className="lm-detail-line">{openUnassigned.items}</p>}
              {openUnassigned.deliveryAddress && <p className="lm-detail-line muted">{openUnassigned.deliveryAddress}</p>}
              {(openUnassigned.customerName || openUnassigned.customerPhone) && (
                <p className="lm-detail-line muted">
                  {openUnassigned.customerName}
                  {openUnassigned.customerName && openUnassigned.customerPhone ? ', ' : ''}
                  {openUnassigned.customerPhone && <a href={`tel:${openUnassigned.customerPhone}`}>{openUnassigned.customerPhone}</a>}
                </p>
              )}
              {!hasPoint(openUnassigned.dropoffLat, openUnassigned.dropoffLng) && (
                <p className="lm-detail-line muted">Teslim noktasının konumu yok; haritada gösterilemiyor.</p>
              )}
              {canAssign && (
                <button className="btn btn-primary lm-assign-btn" onClick={() => requestTransfer(openUnassigned.deliveryId, null)}>
                  <UserPlus size={15} /> Kurye ata
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {transferTarget && (
        <AssignCourierModal
          target={transferTarget}
          fallbackCouriers={fallbackCouriers}
          onClose={() => setTransferTarget(null)}
          onDone={() => { setTransferTarget(null); afterChange(); }}
        />
      )}

      {endShiftTarget && (
        <EndShiftModal
          courier={endShiftTarget}
          busy={actionBusyId === endShiftTarget.courierId}
          onClose={() => setEndShiftTarget(null)}
          onConfirm={confirmEndShift}
        />
      )}

      {confirm && (
        <div className="modal-overlay" onClick={() => !confirmBusy && setConfirm(null)}>
          <div className="modal lm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirm.kind === 'unassign' ? 'Paketi kuryeden al' : 'Kurye adına yola çıkar'}</h2>
              <button className="modal-close" onClick={() => setConfirm(null)} disabled={confirmBusy} aria-label="Kapat"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="lm-confirm">
                <AlertCircle size={34} />
                <strong>{confirm.subtitle}</strong>
                {confirm.kind === 'unassign' ? (
                  <>
                    <p>Paket <strong>{confirm.courierName}</strong> üzerinden alınacak ve atanmamış paketlere dönecek. Kuryeye bildirim gider.</p>
                    <p className="lm-confirm-note">Bu pakete sistem kendiliğinden kurye seçmez; siz bir kuryeye atayana kadar bekler.</p>
                  </>
                ) : (
                  <>
                    <p>{confirm.deliveryIds.length === 1 ? 'Paket' : `${confirm.deliveryIds.length} paket`} <strong>{confirm.courierName}</strong> adına teslim alındı ve yolda olarak işaretlenecek. Paket kuryede kalır; kuryeye bildirim gider.</p>
                    <p className="lm-confirm-note">Pazaryeri siparişinde müşteriye de “yolda” bilgisi gider. Kurye paketi gerçekten aldıysa kullanın; teslim edildi bilgisini yine kurye girer.</p>
                  </>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setConfirm(null)} disabled={confirmBusy}>Vazgeç</button>
              <button className={`btn ${confirm.kind === 'unassign' ? 'btn-danger' : 'btn-success'}`} onClick={runConfirm} disabled={confirmBusy}>
                {confirmBusy ? 'İşleniyor…' : (confirm.kind === 'unassign' ? 'Kuryeden al' : 'Yola çıkar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveMapPage;
